import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    App,
    Button,
    Col,
    Form,
    Input,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from "antd";
import {
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    EyeOutlined,
    PlusOutlined,
    SearchOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
    FilterActions,
    FilterBar,
    FilterField,
} from "../../components/common/FilterBar";
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from "../../components/common/PaginatedListTable";
import { PageLayout } from "../../components/layout";
import { RegionSelect } from "../../components/common/RegionSelect";
import { useDedupedMessage } from "../../hooks/useDedupedMessage";
import { usePermissions } from "../../hooks/usePermissions";
import { getProjectViewerAuthorizationOverview } from "../../services/projectManagementService";
import {
    createProject,
    deleteProject,
    getProjectDeviceReassignmentSummary,
    listProjectOwnerCandidates,
    listProjects,
    listProjectDeleteDevices,
    listRootProjectOwnerCandidates,
    updateProject,
    updateProjectStatus,
} from "../../services/projectService";
import { deleteDevice } from "../../services/deviceService";
import type {
    Project,
    ProjectDeleteDeviceItem,
    ProjectDeviceReassignmentSummary,
    ProjectOwnerCandidate,
} from "../../types/project";
import {
    filterRegionSelectOption,
    matchesRegionPathKeyword,
} from "../../utils/regionSearch";
import type { ViewerAccountSummary } from "../../types/projectManagement";
import { getLocalizedApiErrorMessage } from "../../utils/apiErrorI18n";

interface ProjectManagementV1Props {
    embedded?: boolean;
}

interface ProjectListRow extends Project {
    ownerDisplayName: string;
    ownerUsername: string;
    regionDisplayName: string;
    deviceCount: number;
    treeLevel?: number;
    children?: ProjectListRow[];
}

interface AuthorizedViewerRow {
    userId: number;
    displayName: string;
    username: string;
    organizationName: string;
    authorizedProjectCount: number;
    status: "active" | "inactive";
}

interface ProjectListFilters {
    nameKeyword: string;
    ownerDisplayNameKeyword: string;
    ownerUsernameKeyword: string;
    regionFilter: "all" | string;
    statusFilter: "all" | Project["status"];
}

type ProjectFormValues = {
    name: string;
    owner_user_id?: number;
    region_id?: number;
};

type StatusModalState = {
    open: boolean;
    project: ProjectListRow | null;
    targetStatus: Project["status"] | null;
    submitting: boolean;
};

type DeleteModalState = {
    open: boolean;
    project: ProjectListRow | null;
    returnToTreeRootProject: ProjectListRow | null;
};

type ProjectDeleteConfirmState = {
    open: boolean;
    project: ProjectListRow | null;
    returnToTreeRootProject: ProjectListRow | null;
    anchorKey: string | null;
};

type ProjectDeleteOptions = {
    returnToTreeRootProject?: ProjectListRow | null;
    confirmAnchorKey?: string;
};

type ProjectTreeModalState = {
    open: boolean;
    rootProject: ProjectListRow | null;
};

const getOwnerDisplayName = (
    candidate?: ProjectOwnerCandidate | null,
): string => candidate?.display_name?.trim() || candidate?.username || "-";

const normalizeText = (value?: string | null): string =>
    (value || "").trim().toLowerCase();
const REGION_PATH_SEPARATOR = " / ";

const buildRegionPathOptions = (
    regionPaths: Array<string | null | undefined>,
): string[] => {
    const options = new Set<string>();

    regionPaths.forEach((regionPath) => {
        const parts = (regionPath || "")
            .split(REGION_PATH_SEPARATOR)
            .map((part) => part.trim())
            .filter(Boolean);

        parts.forEach((_, index) => {
            options.add(parts.slice(0, index + 1).join(REGION_PATH_SEPARATOR));
        });
    });

    return Array.from(options).sort((a, b) => a.localeCompare(b));
};

const matchesRegionPath = (
    projectRegionPath: string,
    selectedRegionPath: string,
): boolean =>
    projectRegionPath === selectedRegionPath ||
    projectRegionPath.startsWith(
        `${selectedRegionPath}${REGION_PATH_SEPARATOR}`,
    ) ||
    matchesRegionPathKeyword(projectRegionPath, selectedRegionPath);

const EMPTY_STATUS_MODAL: StatusModalState = {
    open: false,
    project: null,
    targetStatus: null,
    submitting: false,
};

const EMPTY_DELETE_MODAL: DeleteModalState = {
    open: false,
    project: null,
    returnToTreeRootProject: null,
};

const EMPTY_PROJECT_DELETE_CONFIRM: ProjectDeleteConfirmState = {
    open: false,
    project: null,
    returnToTreeRootProject: null,
    anchorKey: null,
};

const EMPTY_PROJECT_TREE_MODAL: ProjectTreeModalState = {
    open: false,
    rootProject: null,
};

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
const DELETE_DEVICE_SELECT_ALL_PAGE_SIZE = 100;
const DELETE_DEVICE_TABLE_SCROLL_Y = 300;
const PROJECT_NAME_MAX_LENGTH = 255;
const PROJECT_TABLE_SCROLL_X = 1350;
const DEFAULT_PROJECT_LIST_FILTERS: ProjectListFilters = {
    nameKeyword: "",
    ownerDisplayNameKeyword: "",
    ownerUsernameKeyword: "",
    regionFilter: "all",
    statusFilter: "all",
};

const isFormValidationError = (
    error: unknown,
): error is { errorFields: unknown[] } =>
    typeof error === "object" && error !== null && "errorFields" in error;

const ProjectManagementV1: React.FC<ProjectManagementV1Props> = ({
    embedded = false,
}) => {
    const { message: rawMessage } = App.useApp();
    const message = useDedupedMessage(rawMessage);
    const { t } = useTranslation(["admin", "navigation", "common"]);
    const formatApiError = useCallback(
        (error: unknown, fallback: string) =>
            getLocalizedApiErrorMessage(error, fallback, t),
        [t],
    );
    const {
        canAccessProjectManagement,
        canCreateProjects,
        canDeleteDevices,
        canDeleteProjects,
        canReadProjectViewerScopes,
        canReadProjects,
        canUpdateProjects,
        canUpdateProjectStatus,
        canManageProjects,
        isPlatformAccount,
        isProjectScopedAccount,
    } = usePermissions();
    const [form] = Form.useForm<ProjectFormValues>();
    const isProjectManagerAccount =
        canManageProjects && isProjectScopedAccount;

    const [projects, setProjects] = useState<Project[]>([]);
    const [viewerAccounts, setViewerAccounts] = useState<
        ViewerAccountSummary[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [authorizationLoading, setAuthorizationLoading] = useState(false);
    const [projectModalVisible, setProjectModalVisible] = useState(false);
    const [projectSubmitting, setProjectSubmitting] = useState(false);
    const [authorizationModalVisible, setAuthorizationModalVisible] =
        useState(false);
    const [editingProject, setEditingProject] = useState<Project | null>(null);
    const [authorizationProject, setAuthorizationProject] =
        useState<ProjectListRow | null>(null);
    const [statusModal, setStatusModal] =
        useState<StatusModalState>(EMPTY_STATUS_MODAL);
    const [deleteModal, setDeleteModal] =
        useState<DeleteModalState>(EMPTY_DELETE_MODAL);
    const [projectDeleteConfirm, setProjectDeleteConfirm] =
        useState<ProjectDeleteConfirmState>(EMPTY_PROJECT_DELETE_CONFIRM);
    const [projectTreeModal, setProjectTreeModal] =
        useState<ProjectTreeModalState>(EMPTY_PROJECT_TREE_MODAL);
    const [projectTreeLoading, setProjectTreeLoading] = useState(false);
    const [deleteReassignmentSummary, setDeleteReassignmentSummary] =
        useState<ProjectDeviceReassignmentSummary | null>(null);
    const [deleteReassignmentLoading, setDeleteReassignmentLoading] =
        useState(false);
    const [deleteDeviceLoading, setDeleteDeviceLoading] = useState(false);
    const [deleteDevicesSubmitting, setDeleteDevicesSubmitting] =
        useState(false);
    const [selectingAllDeleteDevices, setSelectingAllDeleteDevices] =
        useState(false);
    const [deleteDeviceList, setDeleteDeviceList] = useState<
        ProjectDeleteDeviceItem[]
    >([]);
    const [deleteDeviceTotal, setDeleteDeviceTotal] = useState(0);
    const [deleteDeviceCurrentPage, setDeleteDeviceCurrentPage] = useState(1);
    const [deleteDevicePageSize, setDeleteDevicePageSize] =
        useState(DEFAULT_PAGE_SIZE);
    const [deleteDeviceKeyword, setDeleteDeviceKeyword] = useState("");
    const [deleteDeviceProjectFilter, setDeleteDeviceProjectFilter] = useState<
        "all" | number
    >("all");
    const [appliedDeleteDeviceKeyword, setAppliedDeleteDeviceKeyword] =
        useState("");
    const [
        appliedDeleteDeviceProjectFilter,
        setAppliedDeleteDeviceProjectFilter,
    ] = useState<"all" | number>("all");
    const [selectedDeleteDeviceIds, setSelectedDeleteDeviceIds] = useState<
        React.Key[]
    >([]);
    const [ownerCandidates, setOwnerCandidates] = useState<
        ProjectOwnerCandidate[]
    >([]);
    const [ownerCandidateMap, setOwnerCandidateMap] = useState<
        Record<number, ProjectOwnerCandidate>
    >({});
    const projectSubmittingRef = useRef(false);
    const deleteDevicesSubmittingRef = useRef(false);

    const [projectCurrentPage, setProjectCurrentPage] = useState(1);
    const [projectPageSize, setProjectPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [authorizationCurrentPage, setAuthorizationCurrentPage] = useState(1);
    const [authorizationPageSize, setAuthorizationPageSize] =
        useState(DEFAULT_PAGE_SIZE);
    const [nameKeyword, setNameKeyword] = useState("");
    const [ownerDisplayNameKeyword, setOwnerDisplayNameKeyword] = useState("");
    const [ownerUsernameKeyword, setOwnerUsernameKeyword] = useState("");
    const [regionFilter, setRegionFilter] = useState<"all" | string>("all");
    const [statusFilter, setStatusFilter] = useState<"all" | Project["status"]>(
        "all",
    );
    const [appliedProjectFilters, setAppliedProjectFilters] =
        useState<ProjectListFilters>(DEFAULT_PROJECT_LIST_FILTERS);

    const mergeOwnerCandidates = useCallback(
        (candidates: ProjectOwnerCandidate[]) => {
            setOwnerCandidateMap((previous) => {
                const next = { ...previous };
                candidates.forEach((candidate) => {
                    next[candidate.id] = candidate;
                });
                return next;
            });
        },
        [],
    );

    const loadPageData = useCallback(async () => {
        setLoading(true);
        try {
            const projectList = await listProjects();
            setProjects(projectList);
        } catch (error) {
            console.error(error);
            message.error(
                t("admin:project.messages.loadFailed", {
                    defaultValue: "加载项目列表失败",
                }),
            );
        } finally {
            setLoading(false);
        }
    }, [message, t]);

    useEffect(() => {
        if (canReadProjects) {
            void loadPageData();
        }
    }, [canReadProjects, loadPageData]);

    const buildProjectListRow = useCallback(
        (project: Project): ProjectListRow => {
            const ownerCandidate = project.owner_user_id
                ? ownerCandidateMap[project.owner_user_id]
                : undefined;
            const ownerUsername =
                project.owner_username ||
                ownerCandidate?.username ||
                (project.owner_user_id ? `user_${project.owner_user_id}` : "-");

            return {
                ...project,
                ownerDisplayName:
                    project.owner_display_name ||
                    (ownerUsername === "-"
                        ? "-"
                        : getOwnerDisplayName(ownerCandidate)),
                ownerUsername,
                regionDisplayName: project.region_display_name || "-",
                deviceCount: project.device_count ?? 0,
            };
        },
        [ownerCandidateMap],
    );

    const rows = useMemo<ProjectListRow[]>(() => {
        return projects.map(buildProjectListRow);
    }, [buildProjectListRow, projects]);

    const visibleRows = useMemo<ProjectListRow[]>(() => {
        if (isProjectManagerAccount) {
            return rows;
        }
        return rows.filter((project) => project.parent_id === null);
    }, [isProjectManagerAccount, rows]);
    const childProjectRowsByParentId = useMemo(() => {
        const childrenByParentId = new Map<number | null, ProjectListRow[]>();
        rows.forEach((project) => {
            const parentId = project.parent_id ?? null;
            const children = childrenByParentId.get(parentId) || [];
            children.push(project);
            childrenByParentId.set(parentId, children);
        });
        return childrenByParentId;
    }, [rows]);
    const buildProjectTreeRow = useCallback(
        (project: ProjectListRow, level = 0): ProjectListRow => {
            const children = childProjectRowsByParentId
                .get(project.id)
                ?.map((child) => buildProjectTreeRow(child, level + 1));
            return {
                ...project,
                treeLevel: level,
                children:
                    children && children.length > 0 ? children : undefined,
            };
        },
        [childProjectRowsByParentId],
    );
    const projectTreeRows = useMemo<ProjectListRow[]>(
        () =>
            projectTreeModal.rootProject
                ? [buildProjectTreeRow(projectTreeModal.rootProject)]
                : [],
        [buildProjectTreeRow, projectTreeModal.rootProject],
    );
    const projectTreeExpandedRowKeys = useMemo<React.Key[]>(() => {
        const keys: React.Key[] = [];
        const collectExpandedKeys = (items: ProjectListRow[]) => {
            items.forEach((item) => {
                if (item.children?.length) {
                    keys.push(item.id);
                    collectExpandedKeys(item.children);
                }
            });
        };

        collectExpandedKeys(projectTreeRows);
        return keys;
    }, [projectTreeRows]);
    const projectRowsById = useMemo(() => {
        const projectsById = new Map<number, ProjectListRow>();
        rows.forEach((project) => {
            projectsById.set(project.id, project);
        });
        return projectsById;
    }, [rows]);
    const isCurrentScopeRootProject = useCallback(
        (project?: ProjectListRow | null): boolean => {
            if (!project) {
                return false;
            }
            if ((project.treeLevel ?? null) === 0 || project.parent_id == null) {
                return true;
            }
            return !projectRowsById.has(project.parent_id);
        },
        [projectRowsById],
    );
    const canWriteProjectRow = useCallback(
        (project: ProjectListRow): boolean =>
            !isProjectManagerAccount || project.parent_id !== null,
        [isProjectManagerAccount],
    );

    const isChildProjectForm =
        Boolean(editingProject?.parent_id) ||
        (!editingProject && isProjectManagerAccount);
    const ownerFieldHelp = isChildProjectForm
        ? t("admin:project.modal.childOwnerHelp")
        : t("admin:project.modal.ownerHelp");
    const createProjectOwnerAlert = isChildProjectForm
        ? t("admin:project.modal.childOwnerNotice")
        : t("admin:project.modal.ownerNotice");

    const regionOptions = useMemo(
        () =>
            buildRegionPathOptions(
                visibleRows
                    .map((row) => row.regionDisplayName)
                    .filter((value) => value && value !== "-"),
            ),
        [visibleRows],
    );

    const filteredProjects = useMemo(() => {
        const normalizedNameKeyword = normalizeText(
            appliedProjectFilters.nameKeyword,
        );
        const normalizedOwnerNameKeyword = normalizeText(
            appliedProjectFilters.ownerDisplayNameKeyword,
        );
        const normalizedOwnerUsernameKeyword = normalizeText(
            appliedProjectFilters.ownerUsernameKeyword,
        );

        return visibleRows.filter((project) => {
            const matchesName =
                !normalizedNameKeyword ||
                normalizeText(project.name).includes(normalizedNameKeyword);
            const matchesOwnerDisplayName =
                !normalizedOwnerNameKeyword ||
                normalizeText(project.ownerDisplayName).includes(
                    normalizedOwnerNameKeyword,
                );
            const matchesOwnerUsername =
                !normalizedOwnerUsernameKeyword ||
                normalizeText(project.ownerUsername).includes(
                    normalizedOwnerUsernameKeyword,
                );
            const matchesRegion =
                appliedProjectFilters.regionFilter === "all" ||
                matchesRegionPath(
                    project.regionDisplayName,
                    appliedProjectFilters.regionFilter,
                );
            const matchesStatus =
                appliedProjectFilters.statusFilter === "all" ||
                project.status === appliedProjectFilters.statusFilter;

            return (
                matchesName &&
                matchesOwnerDisplayName &&
                matchesOwnerUsername &&
                matchesRegion &&
                matchesStatus
            );
        });
    }, [appliedProjectFilters, visibleRows]);

    const authorizedViewerRows = useMemo<AuthorizedViewerRow[]>(() => {
        if (!authorizationProject) {
            return [];
        }

        return viewerAccounts
            .filter((viewer) =>
                viewer.authorizedProjects.some(
                    (authorizedProject) =>
                        authorizedProject.projectKey ===
                        authorizationProject.code,
                ),
            )
            .map((viewer) => ({
                userId: viewer.userId,
                displayName: viewer.displayName || viewer.username,
                username: viewer.username,
                organizationName: viewer.organizationName || "-",
                authorizedProjectCount: viewer.authorizedProjects.length,
                status: viewer.status,
            }));
    }, [authorizationProject, viewerAccounts]);

    useEffect(() => {
        setProjectCurrentPage(1);
    }, [
        nameKeyword,
        ownerDisplayNameKeyword,
        ownerUsernameKeyword,
        regionFilter,
        statusFilter,
    ]);

    const loadOwnerCandidates = useCallback(
        async (project?: Project | null) => {
            const candidates =
                isPlatformAccount && !isProjectManagerAccount && !project
                    ? await listRootProjectOwnerCandidates()
                    : await listProjectOwnerCandidates(project?.id ?? 0);
            setOwnerCandidates(candidates);
            mergeOwnerCandidates(candidates);
        },
        [isPlatformAccount, isProjectManagerAccount, mergeOwnerCandidates],
    );

    const openCreateModal = useCallback(async () => {
        setEditingProject(null);
        form.resetFields();

        try {
            await loadOwnerCandidates(null);
            setProjectModalVisible(true);
        } catch (error) {
            console.error(error);
            setOwnerCandidates([]);
            message.error(t("admin:project.messages.ownerLoadFailed"));
        }
    }, [form, loadOwnerCandidates, message, t]);

    const openEditModal = useCallback(
        async (project: Project) => {
            setEditingProject(project);
            form.setFieldsValue({
                name: project.name,
                owner_user_id: project.owner_user_id ?? undefined,
                region_id: project.region_id ?? undefined,
            });

            try {
                await loadOwnerCandidates(project);
                setProjectModalVisible(true);
            } catch (error) {
                console.error(error);
                setOwnerCandidates([]);
                message.error(t("admin:project.messages.ownerLoadFailed"));
            }
        },
        [form, loadOwnerCandidates, message, t],
    );

    const closeProjectModal = useCallback(() => {
        projectSubmittingRef.current = false;
        setProjectSubmitting(false);
        setProjectModalVisible(false);
        setEditingProject(null);
        setOwnerCandidates([]);
        form.resetFields();
    }, [form]);

    const openAuthorizationModal = useCallback(
        async (project: ProjectListRow) => {
            setAuthorizationProject(project);
            setAuthorizationModalVisible(true);
            if (!canReadProjectViewerScopes) {
                setViewerAccounts([]);
                return;
            }

            setAuthorizationLoading(true);
            try {
                const overview = await getProjectViewerAuthorizationOverview();
                setViewerAccounts(overview.viewerAccounts);
            } catch (error) {
                console.error(error);
                setViewerAccounts([]);
                message.error(
                    t("admin:project.messages.authorizationLoadFailed"),
                );
            } finally {
                setAuthorizationLoading(false);
            }
        },
        [canReadProjectViewerScopes, message, t],
    );

    const closeAuthorizationModal = useCallback(() => {
        setAuthorizationModalVisible(false);
        setAuthorizationProject(null);
        setViewerAccounts([]);
        setAuthorizationCurrentPage(1);
        setAuthorizationPageSize(DEFAULT_PAGE_SIZE);
    }, []);

    const openStatusModal = useCallback((project: ProjectListRow) => {
        setStatusModal({
            open: true,
            project,
            targetStatus: project.status === "enabled" ? "disabled" : "enabled",
            submitting: false,
        });
    }, []);

    const openProjectTreeModal = useCallback(
        async (project: ProjectListRow) => {
            setProjectTreeModal({
                open: true,
                rootProject: null,
            });
            setProjectTreeLoading(true);
            try {
                const projectList = await listProjects();
                setProjects(projectList);
                const refreshedRows = projectList.map(buildProjectListRow);
                const refreshedProject = refreshedRows.find(
                    (row) => row.id === project.id,
                );

                if (!refreshedProject) {
                    setProjectTreeModal(EMPTY_PROJECT_TREE_MODAL);
                    message.warning(
                        t("admin:project.messages.projectChanged", {
                            defaultValue:
                                "项目已被删除或不在当前账号范围内，请刷新后重试。",
                        }),
                    );
                    return;
                }

                setProjectTreeModal({
                    open: true,
                    rootProject: refreshedProject,
                });
            } catch (error) {
                console.error(error);
                setProjectTreeModal(EMPTY_PROJECT_TREE_MODAL);
                message.error(
                    t("admin:project.messages.loadFailed", {
                        defaultValue: "加载项目列表失败",
                    }),
                );
            } finally {
                setProjectTreeLoading(false);
            }
        },
        [buildProjectListRow, message, t],
    );

    const closeProjectTreeModal = useCallback(() => {
        setProjectTreeModal(EMPTY_PROJECT_TREE_MODAL);
    }, []);

    const closeStatusModal = useCallback(() => {
        setStatusModal((previous) =>
            previous.submitting ? previous : EMPTY_STATUS_MODAL,
        );
    }, []);

    const loadDeleteReassignmentSummary = useCallback(
        async (project: ProjectListRow) => {
            setDeleteReassignmentLoading(true);
            try {
                const summary = await getProjectDeviceReassignmentSummary(
                    project.id,
                );
                setDeleteReassignmentSummary(summary);
                setDeleteModal((previous) =>
                    previous.project?.id === project.id
                        ? {
                              ...previous,
                              project: {
                                  ...previous.project,
                                  deviceCount: summary.total_device_count,
                              },
                          }
                        : previous,
                );
                return summary;
            } catch (error) {
                console.error(error);
                setDeleteReassignmentSummary(null);
                message.error(
                    formatApiError(
                        error,
                        t("admin:project.messages.reassignmentSummaryFailed", {
                            defaultValue: "加载设备归属信息失败",
                        }),
                    ),
                );
                return null;
            } finally {
                setDeleteReassignmentLoading(false);
            }
        },
        [formatApiError, message, t],
    );

    const resetDeleteDeviceState = useCallback(() => {
        setDeleteDeviceList([]);
        setDeleteDeviceTotal(0);
        setDeleteDeviceCurrentPage(1);
        setDeleteDevicePageSize(DEFAULT_PAGE_SIZE);
        setDeleteDeviceKeyword("");
        setDeleteDeviceProjectFilter("all");
        setAppliedDeleteDeviceKeyword("");
        setAppliedDeleteDeviceProjectFilter("all");
        setSelectedDeleteDeviceIds([]);
        setDeleteDevicesSubmitting(false);
        setSelectingAllDeleteDevices(false);
        deleteDevicesSubmittingRef.current = false;
    }, []);

    const loadDeleteDevices = useCallback(
        async (
            project: ProjectListRow,
            options?: {
                page?: number;
                pageSize?: number;
                keyword?: string;
                projectFilter?: "all" | number;
            },
        ) => {
            const nextPage = options?.page ?? deleteDeviceCurrentPage;
            const nextPageSize = options?.pageSize ?? deleteDevicePageSize;
            const nextKeyword = options?.keyword ?? appliedDeleteDeviceKeyword;
            const nextProjectFilter =
                options?.projectFilter ?? appliedDeleteDeviceProjectFilter;

            setDeleteDeviceLoading(true);
            try {
                const result = await listProjectDeleteDevices(project.id, {
                    page: nextPage,
                    page_size: nextPageSize,
                    search_name: nextKeyword,
                    source_project_id:
                        nextProjectFilter === "all"
                            ? undefined
                            : nextProjectFilter,
                });
                setDeleteDeviceList(result.items);
                setDeleteDeviceTotal(result.total);
                setDeleteDeviceCurrentPage(result.page);
                setDeleteDevicePageSize(result.page_size);
            } catch (error) {
                console.error(error);
                setDeleteDeviceList([]);
                setDeleteDeviceTotal(0);
                message.error(
                    formatApiError(
                        error,
                        t("admin:project.messages.deleteDeviceListFailed", {
                            defaultValue: "加载设备列表失败",
                        }),
                    ),
                );
            } finally {
                setDeleteDeviceLoading(false);
            }
        },
        [
            appliedDeleteDeviceKeyword,
            appliedDeleteDeviceProjectFilter,
            deleteDeviceCurrentPage,
            deleteDevicePageSize,
            formatApiError,
            message,
            t,
        ],
    );

    const showProjectDeleteConfirm = useCallback(
        (project: ProjectListRow, options?: ProjectDeleteOptions) => {
            setProjectDeleteConfirm({
                open: true,
                project,
                returnToTreeRootProject:
                    options?.returnToTreeRootProject ?? null,
                anchorKey:
                    options?.confirmAnchorKey ?? `project-list-${project.id}`,
            });
        },
        [],
    );

    const dismissProjectDeleteConfirm = useCallback(() => {
        setProjectDeleteConfirm(EMPTY_PROJECT_DELETE_CONFIRM);
    }, []);

    const closeProjectDeleteConfirm = useCallback(() => {
        const returnToTreeRootProject =
            projectDeleteConfirm.returnToTreeRootProject;
        dismissProjectDeleteConfirm();
        if (returnToTreeRootProject) {
            void openProjectTreeModal(returnToTreeRootProject);
        }
    }, [
        dismissProjectDeleteConfirm,
        openProjectTreeModal,
        projectDeleteConfirm.returnToTreeRootProject,
    ]);

    const confirmProjectDelete = useCallback(
        async (project: ProjectListRow) => {
            try {
                await deleteProject(project.id);
                message.success(
                    t("admin:project.messages.deleteSuccess", {
                        defaultValue: "项目删除成功",
                    }),
                );
                dismissProjectDeleteConfirm();
                setDeleteModal(EMPTY_DELETE_MODAL);
                setDeleteReassignmentSummary(null);
                closeProjectTreeModal();
                await loadPageData();
            } catch (error) {
                console.error(error);
                message.error(
                    formatApiError(
                        error,
                        t("admin:project.messages.deleteFailed", {
                            defaultValue: "项目删除失败",
                        }),
                    ),
                );
                throw error;
            }
        },
        [
            closeProjectTreeModal,
            dismissProjectDeleteConfirm,
            formatApiError,
            loadPageData,
            message,
            t,
        ],
    );

    const openDeleteModal = useCallback(
        async (
            project: ProjectListRow,
            options?: ProjectDeleteOptions,
        ) => {
            resetDeleteDeviceState();
            dismissProjectDeleteConfirm();
            setDeleteReassignmentSummary(null);
            const summary = await loadDeleteReassignmentSummary(project);
            if (!summary) {
                return;
            }
            if (summary.total_device_count <= 0) {
                showProjectDeleteConfirm(project, options);
                return;
            }

            closeProjectTreeModal();
            setDeleteModal({
                open: true,
                project: {
                    ...project,
                    deviceCount: summary.total_device_count,
                },
                returnToTreeRootProject:
                    options?.returnToTreeRootProject ?? null,
            });
            void loadDeleteDevices(project, {
                page: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                keyword: "",
                projectFilter: "all",
            });
        },
        [
            closeProjectTreeModal,
            closeProjectDeleteConfirm,
            loadDeleteDevices,
            loadDeleteReassignmentSummary,
            resetDeleteDeviceState,
            showProjectDeleteConfirm,
        ],
    );

    const renderProjectDeleteButton = useCallback(
        (project: ProjectListRow, options?: ProjectDeleteOptions) => {
            const confirmAnchorKey =
                options?.confirmAnchorKey ?? `project-list-${project.id}`;
            const button = (
                <Button
                    size="small"
                    type="link"
                    danger
                    icon={<DeleteOutlined aria-hidden />}
                    onClick={() =>
                        openDeleteModal(project, {
                            ...options,
                            confirmAnchorKey,
                        })
                    }
                >
                    {t("admin:project.actions.delete", {
                        defaultValue: "删除",
                    })}
                </Button>
            );

            if (
                !projectDeleteConfirm.open ||
                projectDeleteConfirm.anchorKey !== confirmAnchorKey
            ) {
                return button;
            }

            return (
                <Popconfirm
                    open
                    title={t("admin:project.deleteModal.title", {
                        name: projectDeleteConfirm.project?.name ?? project.name,
                        defaultValue: project.name
                            ? `确认删除项目“${project.name}”？`
                            : "确认删除项目？",
                    })}
                    okText={t("common:buttons.ok", {
                        defaultValue: "确定",
                    })}
                    cancelText={t("common:buttons.cancel", {
                        defaultValue: "取消",
                    })}
                    onConfirm={() =>
                        confirmProjectDelete(
                            projectDeleteConfirm.project ?? project,
                        )
                    }
                    onCancel={closeProjectDeleteConfirm}
                    onOpenChange={(open) => {
                        if (!open) {
                            closeProjectDeleteConfirm();
                        }
                    }}
                >
                    {button}
                </Popconfirm>
            );
        },
        [
            closeProjectDeleteConfirm,
            confirmProjectDelete,
            openDeleteModal,
            projectDeleteConfirm,
            t,
        ],
    );

    const closeDeleteModal = useCallback(() => {
        const returnToTreeRootProject = deleteModal.returnToTreeRootProject;
        setDeleteModal(EMPTY_DELETE_MODAL);
        setDeleteReassignmentSummary(null);
        setDeleteReassignmentLoading(false);
        setDeleteDeviceLoading(false);
        resetDeleteDeviceState();
        if (returnToTreeRootProject) {
            void openProjectTreeModal(returnToTreeRootProject);
        }
    }, [
        deleteModal.returnToTreeRootProject,
        openProjectTreeModal,
        resetDeleteDeviceState,
    ]);

    const confirmStatusChange = useCallback(async () => {
        if (!statusModal.project || !statusModal.targetStatus) {
            return;
        }

        setStatusModal((previous) => ({ ...previous, submitting: true }));
        try {
            await updateProjectStatus(
                statusModal.project.id,
                statusModal.targetStatus,
            );
            message.success(t("admin:project.messages.statusUpdateSuccess"));
            setStatusModal(EMPTY_STATUS_MODAL);
            await loadPageData();
        } catch (error) {
            console.error(error);
            message.error(
                t("admin:project.messages.operationFailed", {
                    defaultValue: "项目操作失败",
                }),
            );
            setStatusModal(EMPTY_STATUS_MODAL);
        }
    }, [
        loadPageData,
        message,
        statusModal.project,
        statusModal.targetStatus,
        t,
    ]);

    const applyDeleteDeviceFilters = useCallback(() => {
        if (!deleteModal.project) {
            return;
        }
        setAppliedDeleteDeviceKeyword(deleteDeviceKeyword);
        setAppliedDeleteDeviceProjectFilter(deleteDeviceProjectFilter);
        setSelectedDeleteDeviceIds([]);
        void loadDeleteDevices(deleteModal.project, {
            page: 1,
            pageSize: deleteDevicePageSize,
            keyword: deleteDeviceKeyword,
            projectFilter: deleteDeviceProjectFilter,
        });
    }, [
        deleteDeviceKeyword,
        deleteDevicePageSize,
        deleteDeviceProjectFilter,
        deleteModal.project,
        loadDeleteDevices,
    ]);

    const resetDeleteDeviceFilters = useCallback(() => {
        if (!deleteModal.project) {
            return;
        }
        setDeleteDeviceKeyword("");
        setDeleteDeviceProjectFilter("all");
        setAppliedDeleteDeviceKeyword("");
        setAppliedDeleteDeviceProjectFilter("all");
        setSelectedDeleteDeviceIds([]);
        void loadDeleteDevices(deleteModal.project, {
            page: 1,
            pageSize: deleteDevicePageSize,
            keyword: "",
            projectFilter: "all",
        });
    }, [deleteDevicePageSize, deleteModal.project, loadDeleteDevices]);

    const refreshDeleteProjectBlockers = useCallback(
        async (project: ProjectListRow) => {
            const summary = await loadDeleteReassignmentSummary(project);
            if (summary && summary.total_device_count <= 0) {
                await loadPageData();
                if (deleteModal.project?.id === project.id) {
                    closeDeleteModal();
                }
                return;
            }
            await loadDeleteDevices(project, {
                page: 1,
                pageSize: deleteDevicePageSize,
                keyword: appliedDeleteDeviceKeyword,
                projectFilter: appliedDeleteDeviceProjectFilter,
            });
            await loadPageData();
        },
        [
            appliedDeleteDeviceKeyword,
            appliedDeleteDeviceProjectFilter,
            deleteDevicePageSize,
            deleteModal.project?.id,
            closeDeleteModal,
            loadDeleteDevices,
            loadDeleteReassignmentSummary,
            loadPageData,
        ],
    );

    const confirmDeleteSelectedDevices = useCallback(() => {
        if (!deleteModal.project) {
            return;
        }
        if (!canDeleteDevices) {
            const isDeletingRootProjectWithoutPermission =
                isCurrentScopeRootProject(deleteModal.project);
            message.warning(
                t(
                    isDeletingRootProjectWithoutPermission
                        ? "admin:project.deleteModal.mainDeleteDevicePermissionRequired"
                        : "admin:project.deleteModal.deleteDevicePermissionRequired",
                    {
                        defaultValue: isDeletingRootProjectWithoutPermission
                            ? "当前账号没有设备删除权限，请联系有权限的人员先处理设备。"
                            : "需要设备删除权限，才能删除设备。",
                    },
                ),
            );
            return;
        }
        const deviceIds = selectedDeleteDeviceIds.map(String);
        if (deviceIds.length === 0) {
            message.warning(
                t("admin:project.deleteModal.selectDevicesFirst", {
                    defaultValue: "请先勾选设备。",
                }),
            );
            return;
        }

        const project = deleteModal.project;
        Modal.confirm({
            title: t("admin:project.deleteDevicesConfirm.title", {
                count: deviceIds.length,
                defaultValue: "确认删除 {{count}} 台设备？",
            }),
            content: t("admin:project.deleteDevicesConfirm.content", {
                count: deviceIds.length,
                defaultValue:
                    "删除后设备不能继续使用，历史记录和图片会保留。设备清空后再删除项目。",
            }),
            okText: t("admin:project.deleteDevicesConfirm.ok", {
                defaultValue: "删除设备",
            }),
            okType: "danger",
            cancelText: t("common:buttons.cancel", {
                defaultValue: "取消",
            }),
            async onOk() {
                if (deleteDevicesSubmittingRef.current) {
                    return;
                }
                deleteDevicesSubmittingRef.current = true;
                setDeleteDevicesSubmitting(true);
                try {
                    for (const deviceId of deviceIds) {
                        await deleteDevice(deviceId);
                    }
                    message.success(
                        t("admin:project.messages.deleteDevicesSuccess", {
                            count: deviceIds.length,
                            defaultValue: "设备删除成功",
                        }),
                    );
                    setSelectedDeleteDeviceIds([]);
                    await refreshDeleteProjectBlockers(project);
                } catch (error) {
                    console.error(error);
                    message.error(
                        formatApiError(
                            error,
                            t("admin:project.messages.deleteDevicesFailed", {
                                defaultValue: "设备删除失败",
                            }),
                        ),
                    );
                } finally {
                    deleteDevicesSubmittingRef.current = false;
                    setDeleteDevicesSubmitting(false);
                }
            },
        });
    }, [
        canDeleteDevices,
        deleteModal.project,
        formatApiError,
        isCurrentScopeRootProject,
        message,
        refreshDeleteProjectBlockers,
        selectedDeleteDeviceIds,
        t,
    ]);

    const isDeletingRootProject = Boolean(
        isCurrentScopeRootProject(deleteModal.project),
    );

    const renderDeleteDeviceProjectName = useCallback(
        (device: ProjectDeleteDeviceItem) => {
            const projectName =
                device.project_name?.trim() ||
                t("admin:project.deleteModal.unassignedProject", {
                    defaultValue: "未分配项目",
                });
            const rootProjectName = device.root_project_name?.trim() || "";
            const shouldShowRootProject =
                rootProjectName.length > 0 && rootProjectName !== projectName;
            const rootProjectDisplayText = t(
                "admin:project.deleteModal.rootProjectName",
                {
                    name: rootProjectName,
                    defaultValue: `总项目: ${rootProjectName}`,
                },
            );

            return (
                <div style={{ minWidth: 0 }}>
                    {renderPaginatedListText(projectName)}
                    {shouldShowRootProject ? (
                        <Typography.Text
                            type="secondary"
                            title={rootProjectDisplayText}
                            style={{
                                display: "block",
                                fontSize: 12,
                                lineHeight: 1.4,
                                marginTop: 2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {rootProjectDisplayText}
                        </Typography.Text>
                    ) : null}
                </div>
            );
        },
        [t],
    );

    const selectAllDeleteDevices = useCallback(async () => {
        if (!deleteModal.project) {
            return;
        }
        if (!canDeleteDevices) {
            message.warning(
                t("admin:project.deleteModal.selectDevicesPermissionRequired", {
                    defaultValue: "当前账号没有处理设备的权限。",
                }),
            );
            return;
        }

        const project = deleteModal.project;
        const keyword = deleteDeviceKeyword;
        const projectFilter = deleteDeviceProjectFilter;
        const sourceProjectId =
            projectFilter === "all" ? undefined : projectFilter;

        setSelectingAllDeleteDevices(true);
        setSelectedDeleteDeviceIds([]);
        setAppliedDeleteDeviceKeyword(keyword);
        setAppliedDeleteDeviceProjectFilter(projectFilter);
        try {
            const firstPage = await listProjectDeleteDevices(project.id, {
                page: 1,
                page_size: DELETE_DEVICE_SELECT_ALL_PAGE_SIZE,
                search_name: keyword,
                source_project_id: sourceProjectId,
            });
            const allIds = firstPage.items.map((device) => device.id);
            const totalPages = Math.ceil(
                firstPage.total / firstPage.page_size,
            );

            for (let page = 2; page <= totalPages; page += 1) {
                const pageResult = await listProjectDeleteDevices(project.id, {
                    page,
                    page_size: DELETE_DEVICE_SELECT_ALL_PAGE_SIZE,
                    search_name: keyword,
                    source_project_id: sourceProjectId,
                });
                allIds.push(...pageResult.items.map((device) => device.id));
            }

            await loadDeleteDevices(project, {
                page: 1,
                pageSize: deleteDevicePageSize,
                keyword,
                projectFilter,
            });

            const selectedIds = Array.from(new Set(allIds));
            setSelectedDeleteDeviceIds(selectedIds);
            if (selectedIds.length === 0) {
                message.warning(
                    t("admin:project.deleteModal.selectAllDevicesEmpty", {
                        defaultValue: "当前条件下没有可选择的设备。",
                    }),
                );
            }
        } catch (error) {
            console.error(error);
            message.error(
                formatApiError(
                    error,
                    t("admin:project.deleteModal.selectAllDevicesFailed", {
                        defaultValue: "选择全部设备失败",
                    }),
                ),
            );
        } finally {
            setSelectingAllDeleteDevices(false);
        }
    }, [
        canDeleteDevices,
        deleteDeviceKeyword,
        deleteDevicePageSize,
        deleteDeviceProjectFilter,
        deleteModal.project,
        formatApiError,
        loadDeleteDevices,
        message,
        t,
    ]);

    const deleteDeviceProjectGroups = useMemo(
        () =>
            (deleteReassignmentSummary?.groups || []).filter(
                (group) => group.device_count > 0,
            ),
        [deleteReassignmentSummary],
    );
    const deleteDeviceProjectOptions = useMemo(
        () => [
            {
                value: "all" as const,
                label: t("admin:project.deleteModal.allProjects", {
                    defaultValue: "全部项目",
                }),
            },
            ...deleteDeviceProjectGroups.map((group) => ({
                value: group.project_id,
                label: `${group.project_name}（${group.device_count}台）`,
            })),
        ],
        [deleteDeviceProjectGroups, t],
    );

    const handleSubmit = useCallback(async () => {
        if (projectSubmittingRef.current) {
            return;
        }

        projectSubmittingRef.current = true;
        setProjectSubmitting(true);
        try {
            const values = await form.validateFields();

            if (editingProject) {
                await updateProject(editingProject.id, values);
                message.success(
                    t("admin:project.messages.updateSuccess", {
                        defaultValue: "项目更新成功",
                    }),
                );
            } else {
                await createProject(values);
                message.success(
                    t("admin:project.messages.createSuccess", {
                        defaultValue: "项目创建成功",
                    }),
                );
            }

            closeProjectModal();
            await loadPageData();
        } catch (error) {
            console.error(error);
            if (isFormValidationError(error)) {
                return;
            }
            message.error(
                formatApiError(
                    error,
                    t("admin:project.messages.operationFailed", {
                        defaultValue: "项目操作失败",
                    }),
                ),
            );
        } finally {
            projectSubmittingRef.current = false;
            setProjectSubmitting(false);
        }
    }, [
        closeProjectModal,
        editingProject,
        form,
        formatApiError,
        loadPageData,
        message,
        t,
    ]);

    const ownerSelectOptions = useMemo(() => {
        const options = ownerCandidates.map((candidate) => ({
            label: t("admin:project.modal.ownerOptionLabel", {
                name: getOwnerDisplayName(candidate),
                username: candidate.username,
            }),
            value: candidate.id,
        }));

        const editingOwnerId = editingProject?.owner_user_id;
        const hasEditingOwnerOption =
            editingOwnerId == null ||
            ownerCandidates.some(
                (candidate) => candidate.id === editingOwnerId,
            );
        if (hasEditingOwnerOption) {
            return options;
        }

        const editingOwnerUsername =
            editingProject?.owner_username?.trim() || "";
        const editingOwnerName =
            editingProject?.owner_display_name?.trim() ||
            editingOwnerUsername ||
            String(editingOwnerId);
        return [
            {
                label: t("admin:project.modal.ownerOptionLabel", {
                    name: editingOwnerName,
                    username: editingOwnerUsername,
                }),
                value: editingOwnerId,
                disabled: true,
            },
            ...options,
        ];
    }, [editingProject, ownerCandidates, t]);

    const statusModalTitle = useMemo(() => {
        if (!statusModal.targetStatus) {
            return "";
        }
        return statusModal.targetStatus === "enabled"
            ? t("admin:project.statusModal.enableTitle")
            : t("admin:project.statusModal.disableTitle");
    }, [statusModal.targetStatus, t]);

    const statusModalDescription = useMemo(() => {
        if (!statusModal.project || !statusModal.targetStatus) {
            return "";
        }
        return statusModal.targetStatus === "enabled"
            ? t("admin:project.statusModal.enableDescription", {
                  name: statusModal.project.name,
              })
            : t("admin:project.statusModal.disableDescription", {
                  name: statusModal.project.name,
              });
    }, [statusModal.project, statusModal.targetStatus, t]);

    const statusActionLabel =
        statusModal.targetStatus === "enabled"
            ? t("admin:project.actions.enable", { defaultValue: "启用" })
            : t("admin:project.actions.disable", { defaultValue: "禁用" });

    const projectNameMaxLengthMessage = t(
        "admin:project.validation.nameMaxLength",
        {
            defaultValue: "项目名称长度不能超过255个字符",
        },
    );
    const projectNameRules = [
        {
            required: true,
            whitespace: true,
            message: t("admin:project.validation.nameRequired"),
        },
        { max: PROJECT_NAME_MAX_LENGTH, message: projectNameMaxLengthMessage },
    ];
    const pageTitle = t("admin:project.title", { defaultValue: "项目管理" });
    const deleteDeviceCount =
        deleteReassignmentSummary?.total_device_count ??
        deleteModal.project?.deviceCount ??
        0;
    const deleteScopeChecking =
        deleteModal.open &&
        deleteReassignmentLoading &&
        !deleteReassignmentSummary &&
        (deleteModal.project?.deviceCount ?? 0) <= 0;
    const deleteBlockedByDevices = deleteDeviceCount > 0;
    const deleteDeviceTableScroll =
        deleteDeviceList.length > DEFAULT_PAGE_SIZE
            ? { y: DELETE_DEVICE_TABLE_SCROLL_Y }
            : undefined;
    const draftProjectFilters: ProjectListFilters = {
        nameKeyword,
        ownerDisplayNameKeyword,
        ownerUsernameKeyword,
        regionFilter,
        statusFilter,
    };
    const applyProjectFilters = () => {
        setAppliedProjectFilters(draftProjectFilters);
        setProjectCurrentPage(1);
        void loadPageData();
    };
    const resetProjectFilters = () => {
        setNameKeyword(DEFAULT_PROJECT_LIST_FILTERS.nameKeyword);
        setOwnerDisplayNameKeyword(
            DEFAULT_PROJECT_LIST_FILTERS.ownerDisplayNameKeyword,
        );
        setOwnerUsernameKeyword(
            DEFAULT_PROJECT_LIST_FILTERS.ownerUsernameKeyword,
        );
        setRegionFilter(DEFAULT_PROJECT_LIST_FILTERS.regionFilter);
        setStatusFilter(DEFAULT_PROJECT_LIST_FILTERS.statusFilter);
        setAppliedProjectFilters(DEFAULT_PROJECT_LIST_FILTERS);
        setProjectCurrentPage(1);
    };

    const content = (
        <>
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <FilterBar data-testid="project-filter-panel">
                    <FilterField
                        label={t("admin:project.filters.projectNameLabel", {
                            defaultValue: "项目名称",
                        })}
                    >
                        <Input
                            allowClear
                            value={nameKeyword}
                            placeholder={t(
                                "admin:project.filters.projectName",
                                { defaultValue: "搜索项目名称" },
                            )}
                            onChange={(event) =>
                                setNameKeyword(event.target.value)
                            }
                            onPressEnter={applyProjectFilters}
                        />
                    </FilterField>
                    <FilterField
                        label={t(
                            "admin:project.filters.ownerDisplayNameLabel",
                            { defaultValue: "负责人姓名" },
                        )}
                    >
                        <Input
                            allowClear
                            value={ownerDisplayNameKeyword}
                            placeholder={t(
                                "admin:project.filters.ownerDisplayName",
                                { defaultValue: "搜索负责人姓名" },
                            )}
                            onChange={(event) =>
                                setOwnerDisplayNameKeyword(event.target.value)
                            }
                            onPressEnter={applyProjectFilters}
                        />
                    </FilterField>
                    <FilterField
                        label={t("admin:project.filters.ownerUsernameLabel", {
                            defaultValue: "负责人账号",
                        })}
                    >
                        <Input
                            allowClear
                            value={ownerUsernameKeyword}
                            placeholder={t(
                                "admin:project.filters.ownerUsername",
                                { defaultValue: "搜索负责人账号" },
                            )}
                            onChange={(event) =>
                                setOwnerUsernameKeyword(event.target.value)
                            }
                            onPressEnter={applyProjectFilters}
                        />
                    </FilterField>
                    <FilterField
                        label={t("admin:project.filters.regionLabel", {
                            defaultValue: "所属区域",
                        })}
                    >
                        <Select
                            value={regionFilter}
                            options={[
                                {
                                    label: t(
                                        "admin:project.filters.allRegion",
                                        { defaultValue: "全部区域" },
                                    ),
                                    value: "all",
                                },
                                ...regionOptions.map((regionName) => ({
                                    label: regionName,
                                    value: regionName,
                                })),
                            ]}
                            showSearch
                            optionFilterProp="label"
                            filterOption={filterRegionSelectOption}
                            onChange={(value) => setRegionFilter(value)}
                        />
                    </FilterField>
                    <FilterField
                        label={t("admin:project.filters.statusLabel", {
                            defaultValue: "状态",
                        })}
                    >
                        <Select
                            value={statusFilter}
                            options={[
                                {
                                    label: t(
                                        "admin:project.filters.allStatus",
                                        { defaultValue: "全部状态" },
                                    ),
                                    value: "all",
                                },
                                {
                                    label: t("admin:project.status.enabled", {
                                        defaultValue: "启用",
                                    }),
                                    value: "enabled",
                                },
                                {
                                    label: t("admin:project.status.disabled", {
                                        defaultValue: "禁用",
                                    }),
                                    value: "disabled",
                                },
                            ]}
                            onChange={(value) => setStatusFilter(value)}
                        />
                    </FilterField>
                    <FilterActions
                        className="project-filter-actions"
                        testId="project-filter-actions"
                    >
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={applyProjectFilters}
                        >
                            {t("common:buttons.search", {
                                defaultValue: "查询",
                            })}
                        </Button>
                        <Button onClick={resetProjectFilters}>
                            {t("common:buttons.reset", {
                                defaultValue: "重置",
                            })}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListContainer>
                    {canCreateProjects ? (
                        <PaginatedListToolbar data-testid="project-list-toolbar">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => void openCreateModal()}
                            >
                                {t("common:buttons.add", {
                                    defaultValue: "添加",
                                })}
                            </Button>
                        </PaginatedListToolbar>
                    ) : null}

                    <PaginatedListCard>
                        <Table<ProjectListRow>
                            rowKey="id"
                            loading={loading}
                            dataSource={filteredProjects}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            tableLayout="fixed"
                            scroll={{ x: PROJECT_TABLE_SCROLL_X }}
                            pagination={buildPaginatedListPagination({
                                current: projectCurrentPage,
                                pageSize: projectPageSize,
                                showTotal: (total) =>
                                    t("admin:project.pagination.total", {
                                        total,
                                        defaultValue:
                                            "Total {{total}} projects",
                                    }),
                                onChange: (page, nextPageSize) => {
                                    const resolvedPageSize =
                                        nextPageSize ?? projectPageSize;
                                    setProjectPageSize(resolvedPageSize);
                                    setProjectCurrentPage(
                                        resolvedPageSize !== projectPageSize
                                            ? 1
                                            : page,
                                    );
                                },
                            })}
                            columns={[
                                {
                                    title: t("admin:project.columns.name", {
                                        defaultValue: "项目名称",
                                    }),
                                    dataIndex: "name",
                                    width: 220,
                                    ellipsis: true,
                                    render: (value: string) => (
                                        <Typography.Text strong ellipsis>
                                            {value}
                                        </Typography.Text>
                                    ),
                                },
                                {
                                    title: t("admin:project.columns.region", {
                                        defaultValue: "所属区域",
                                    }),
                                    dataIndex: "regionDisplayName",
                                    width: 280,
                                    ellipsis: true,
                                },
                                {
                                    title: t(
                                        "admin:project.columns.ownerDisplayName",
                                        { defaultValue: "负责人姓名" },
                                    ),
                                    dataIndex: "ownerDisplayName",
                                    width: 140,
                                    ellipsis: true,
                                },
                                {
                                    title: t(
                                        "admin:project.columns.ownerUsername",
                                        { defaultValue: "负责人账号" },
                                    ),
                                    dataIndex: "ownerUsername",
                                    width: 120,
                                    ellipsis: true,
                                },
                                {
                                    title: t(
                                        "admin:project.columns.deviceCount",
                                        {
                                            defaultValue: "设备数",
                                        },
                                    ),
                                    dataIndex: "deviceCount",
                                    width: 90,
                                },
                                {
                                    title: t("admin:project.columns.status", {
                                        defaultValue: "状态",
                                    }),
                                    dataIndex: "status",
                                    width: 80,
                                    render: (status: Project["status"]) => (
                                        <Tag
                                            color={
                                                status === "enabled"
                                                    ? "green"
                                                    : "red"
                                            }
                                        >
                                            {status === "enabled"
                                                ? t(
                                                      "admin:project.status.enabled",
                                                      { defaultValue: "启用" },
                                                  )
                                                : t(
                                                      "admin:project.status.disabled",
                                                      { defaultValue: "禁用" },
                                                  )}
                                        </Tag>
                                    ),
                                },
                                {
                                    title: t("admin:project.columns.actions", {
                                        defaultValue: "操作",
                                    }),
                                    key: "actions",
                                    width: 380,
                                    fixed: "right" as const,
                                    render: (_, project) => {
                                        const showWriteActions =
                                            canWriteProjectRow(project) &&
                                            (canUpdateProjects ||
                                                canUpdateProjectStatus ||
                                                canDeleteProjects);
                                        const showActions =
                                            showWriteActions ||
                                            canReadProjectViewerScopes;

                                        if (!showActions) {
                                            return null;
                                        }

                                        return (
                                            <Space wrap={false} size={8}>
                                                {showWriteActions &&
                                                    canUpdateProjects && (
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            icon={
                                                                <EditOutlined
                                                                    aria-hidden
                                                                />
                                                            }
                                                            onClick={() =>
                                                                void openEditModal(
                                                                    project,
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                "admin:project.actions.edit",
                                                                {
                                                                    defaultValue:
                                                                        "编辑",
                                                                },
                                                            )}
                                                        </Button>
                                                    )}
                                                {showWriteActions &&
                                                    canUpdateProjectStatus &&
                                                    (project.status ===
                                                    "enabled" ? (
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            danger
                                                            icon={
                                                                <StopOutlined
                                                                    aria-hidden
                                                                />
                                                            }
                                                            onClick={() =>
                                                                openStatusModal(
                                                                    project,
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                "admin:project.actions.disable",
                                                                {
                                                                    defaultValue:
                                                                        "禁用",
                                                                },
                                                            )}
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            icon={
                                                                <CheckCircleOutlined
                                                                    aria-hidden
                                                                />
                                                            }
                                                            onClick={() =>
                                                                openStatusModal(
                                                                    project,
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                "admin:project.actions.enable",
                                                                {
                                                                    defaultValue:
                                                                        "启用",
                                                                },
                                                            )}
                                                        </Button>
                                                    ))}
                                                {canReadProjectViewerScopes && (
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        icon={
                                                            <EyeOutlined
                                                                aria-hidden
                                                            />
                                                        }
                                                        onClick={() =>
                                                            void openAuthorizationModal(
                                                                project,
                                                            )
                                                        }
                                                    >
                                                        {t(
                                                            "admin:project.actions.viewScopes",
                                                            {
                                                                defaultValue:
                                                                    "查看授权",
                                                            },
                                                        )}
                                                    </Button>
                                                )}
                                                {showWriteActions &&
                                                    canDeleteProjects &&
                                                    renderProjectDeleteButton(
                                                        project,
                                                        {
                                                            confirmAnchorKey: `project-list-${project.id}`,
                                                        },
                                                    )}
                                            </Space>
                                        );
                                    },
                                },
                            ]}
                        />
                    </PaginatedListCard>
                </PaginatedListContainer>
            </Space>

            <Modal
                open={projectModalVisible}
                title={
                    editingProject
                        ? t("admin:project.modal.editTitleWithName", {
                              name: editingProject.name,
                          })
                        : t("admin:project.modal.createTitle", {
                              defaultValue: "创建项目",
                          })
                }
                width={editingProject ? 560 : 840}
                okText={
                    editingProject
                        ? t("admin:project.modal.confirmSave")
                        : t("admin:project.modal.confirmCreate", {
                              defaultValue: "确认创建",
                          })
                }
                cancelText={t("common:buttons.cancel", {
                    defaultValue: "取消",
                })}
                onOk={() => void handleSubmit()}
                onCancel={closeProjectModal}
                confirmLoading={projectSubmitting}
                maskClosable={false}
                destroyOnClose
            >
                {editingProject ? (
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="name"
                            label={t("admin:project.form.name", {
                                defaultValue: "项目名称",
                            })}
                            rules={projectNameRules}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="owner_user_id"
                            label={t("admin:project.form.ownerAccount")}
                            rules={[
                                {
                                    required: true,
                                    message: t(
                                        "admin:project.validation.ownerAccountRequired",
                                    ),
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    normalizeText(
                                        String(option?.label || ""),
                                    ).includes(normalizeText(input))
                                }
                                options={ownerSelectOptions}
                            />
                        </Form.Item>
                        <Typography.Text type="secondary">
                            {ownerFieldHelp}
                        </Typography.Text>
                    </Form>
                ) : (
                    <Space
                        direction="vertical"
                        size={16}
                        style={{ width: "100%" }}
                    >
                        <Alert
                            type="info"
                            showIcon
                            message={createProjectOwnerAlert}
                        />
                        <Form form={form} layout="vertical">
                            <Row gutter={[16, 16]}>
                                <Col xs={24}>
                                    <Form.Item
                                        name="name"
                                        label={t("admin:project.form.name", {
                                            defaultValue: "项目名称",
                                        })}
                                        rules={projectNameRules}
                                    >
                                        <Input
                                            placeholder={t(
                                                "admin:project.modal.projectNamePlaceholder",
                                            )}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        name="region_id"
                                        label={t(
                                            "admin:project.modal.addressLabel",
                                        )}
                                        rules={[
                                            {
                                                required: true,
                                                message: t(
                                                    "admin:project.validation.addressRequired",
                                                ),
                                            },
                                        ]}
                                    >
                                        <RegionSelect
                                            placeholder={t(
                                                "admin:project.modal.addressPlaceholder",
                                            )}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        name="owner_user_id"
                                        label={t("admin:project.form.owner", {
                                            defaultValue: "项目负责人",
                                        })}
                                        rules={[
                                            {
                                                required: true,
                                                message: t(
                                                    "admin:project.validation.ownerRequired",
                                                ),
                                            },
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            filterOption={(input, option) =>
                                                normalizeText(
                                                    String(option?.label || ""),
                                                ).includes(normalizeText(input))
                                            }
                                            placeholder={t(
                                                "admin:project.modal.ownerPlaceholder",
                                            )}
                                            options={ownerSelectOptions}
                                        />
                                    </Form.Item>
                                    <Typography.Text type="secondary">
                                        {ownerFieldHelp}
                                    </Typography.Text>
                                </Col>
                            </Row>
                        </Form>
                    </Space>
                )}
            </Modal>

            <Modal
                open={authorizationModalVisible}
                title={
                    authorizationProject
                        ? t("admin:project.authorization.titleWithName", {
                              name: authorizationProject.name,
                          })
                        : t("admin:project.authorization.title")
                }
                width={960}
                footer={null}
                onCancel={closeAuthorizationModal}
                maskClosable={false}
                destroyOnClose
            >
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Alert
                        type="info"
                        showIcon
                        message={t("admin:project.authorization.notice")}
                    />
                    <Table<AuthorizedViewerRow>
                        rowKey="userId"
                        dataSource={authorizedViewerRows}
                        loading={authorizationLoading}
                        size="small"
                        pagination={{
                            current: authorizationCurrentPage,
                            pageSize: authorizationPageSize,
                            showSizeChanger: true,
                            pageSizeOptions: PAGE_SIZE_OPTIONS,
                            showTotal: (total) =>
                                t(
                                    "admin:project.authorization.pagination.total",
                                    {
                                        total,
                                        defaultValue:
                                            "Total {{total}} accounts",
                                    },
                                ),
                            onChange: (page, nextPageSize) => {
                                const resolvedPageSize =
                                    nextPageSize ?? authorizationPageSize;
                                setAuthorizationPageSize(resolvedPageSize);
                                setAuthorizationCurrentPage(
                                    resolvedPageSize !== authorizationPageSize
                                        ? 1
                                        : page,
                                );
                            },
                        }}
                        columns={[
                            {
                                title: t(
                                    "admin:project.authorization.columns.name",
                                ),
                                dataIndex: "displayName",
                            },
                            {
                                title: t(
                                    "admin:project.authorization.columns.username",
                                ),
                                dataIndex: "username",
                            },
                            {
                                title: t(
                                    "admin:project.authorization.columns.organization",
                                ),
                                dataIndex: "organizationName",
                            },
                            {
                                title: t(
                                    "admin:project.authorization.columns.authorizedProjectCount",
                                ),
                                dataIndex: "authorizedProjectCount",
                                width: 140,
                            },
                            {
                                title: t(
                                    "admin:project.authorization.columns.status",
                                ),
                                dataIndex: "status",
                                width: 120,
                                render: (status: "active" | "inactive") => (
                                    <Tag
                                        color={
                                            status === "active"
                                                ? "green"
                                                : "default"
                                        }
                                    >
                                        {status === "active"
                                            ? t(
                                                  "admin:project.status.enabled",
                                                  { defaultValue: "启用" },
                                              )
                                            : t(
                                                  "admin:project.status.disabled",
                                                  { defaultValue: "禁用" },
                                              )}
                                    </Tag>
                                ),
                            },
                        ]}
                    />
                </Space>
            </Modal>

            <Modal
                open={projectTreeModal.open}
                title={t("admin:project.treeModal.title", {
                    defaultValue: "选择要删除的项目",
                })}
                width={920}
                footer={
                    <Button onClick={closeProjectTreeModal}>
                        {t("common:buttons.close", {
                            defaultValue: "关闭",
                        })}
                    </Button>
                }
                onCancel={closeProjectTreeModal}
                maskClosable={false}
                destroyOnClose
            >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Alert
                        type="info"
                        showIcon
                        message={t("admin:project.treeModal.notice", {
                            defaultValue:
                                "删除项目前，需要先清空它下面的设备。继续使用的设备先调整归属，不再使用的设备可删除。",
                        })}
                    />
                    <Table<ProjectListRow>
                        rowKey="id"
                        dataSource={projectTreeRows}
                        loading={projectTreeLoading}
                        size="small"
                        pagination={false}
                        expandable={{
                            expandedRowKeys: projectTreeExpandedRowKeys,
                            indentSize: 0,
                            expandIcon: () => null,
                        }}
                        columns={[
                            {
                                title: t(
                                    "admin:project.treeModal.levelColumn",
                                    {
                                        defaultValue: "项目层级",
                                    },
                                ),
                                width: 130,
                                render: (_value: string, project) => {
                                    const level = project.treeLevel ?? 0;
                                    const levelOffset = level * 28;
                                    const levelLabel =
                                        level === 0
                                            ? t(
                                                  "admin:project.treeModal.rootProject",
                                                  {
                                                      defaultValue: "主项目",
                                                  },
                                              )
                                            : t(
                                                  "admin:project.treeModal.childProject",
                                                  {
                                                      level,
                                                      defaultValue:
                                                          "{{level}}级项目",
                                                  },
                                              );
                                    return (
                                        <Space
                                            size={8}
                                            align="center"
                                            style={{
                                                paddingLeft: levelOffset,
                                            }}
                                        >
                                            <span
                                                aria-hidden
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: 999,
                                                    background:
                                                        level === 0
                                                            ? "#1677ff"
                                                            : level === 1
                                                              ? "#4096ff"
                                                              : "#13c2c2",
                                                    display: "inline-block",
                                                }}
                                            />
                                            <Tag
                                                color={
                                                    level === 0
                                                        ? "blue"
                                                        : level === 1
                                                          ? "geekblue"
                                                          : "cyan"
                                                }
                                                style={{ marginInlineEnd: 0 }}
                                            >
                                                {levelLabel}
                                            </Tag>
                                        </Space>
                                    );
                                },
                            },
                            {
                                title: t("admin:project.columns.name", {
                                    defaultValue: "项目名称",
                                }),
                                dataIndex: "name",
                                ellipsis: true,
                                render: (value: string, project) => (
                                    <Typography.Text
                                        strong={(project.treeLevel ?? 0) === 0}
                                        ellipsis
                                    >
                                        {value}
                                    </Typography.Text>
                                ),
                            },
                            {
                                title: t("admin:project.columns.actions", {
                                    defaultValue: "操作",
                                }),
                                key: "actions",
                                width: 120,
                                render: (_, project) =>
                                    canDeleteProjects &&
                                    canWriteProjectRow(project) ? (
                                        renderProjectDeleteButton(project, {
                                            returnToTreeRootProject:
                                                projectTreeModal.rootProject,
                                            confirmAnchorKey: `project-tree-${project.id}`,
                                        })
                                    ) : null,
                            },
                        ]}
                    />
                </Space>
            </Modal>

            <Modal
                open={statusModal.open}
                footer={null}
                closable={false}
                onCancel={closeStatusModal}
                width={430}
                maskClosable={false}
                destroyOnClose
            >
                <div style={{ display: "flex", gap: 12 }}>
                    <ExclamationCircleFilled
                        style={{
                            color: "#faad14",
                            fontSize: 22,
                            marginTop: 4,
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontSize: 24,
                                fontWeight: 600,
                                color: "#262626",
                                lineHeight: 1.5,
                                marginBottom: 12,
                            }}
                        >
                            {statusModalTitle}
                        </div>
                        <div
                            style={{
                                fontSize: 16,
                                color: "#595959",
                                lineHeight: 1.75,
                                marginBottom: 24,
                            }}
                        >
                            {statusModalDescription}
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 12,
                            }}
                        >
                            <Button
                                onClick={closeStatusModal}
                                disabled={statusModal.submitting}
                            >
                                {t("common:buttons.cancel", {
                                    defaultValue: "取消",
                                })}
                            </Button>
                            <Button
                                type="primary"
                                danger={statusModal.targetStatus === "disabled"}
                                loading={statusModal.submitting}
                                onClick={() => void confirmStatusChange()}
                            >
                                {statusActionLabel}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                open={deleteModal.open}
                footer={null}
                closable
                onCancel={closeDeleteModal}
                width={deleteBlockedByDevices ? 980 : 460}
                style={deleteBlockedByDevices ? { top: 56 } : undefined}
                maskClosable={false}
                destroyOnClose
                styles={{
                    body: deleteBlockedByDevices
                        ? {
                              overflowY: "hidden",
                          }
                        : {
                              maxHeight: "68vh",
                              overflowY: "auto",
                          },
                }}
            >
                <div>
                    <div>
                        <div
                            style={{
                                fontSize: 24,
                                fontWeight: 600,
                                color: "#262626",
                                lineHeight: 1.5,
                                marginBottom: 12,
                            }}
                        >
                            {deleteScopeChecking
                                ? t("admin:project.deleteModal.checkingTitle", {
                                      defaultValue: "正在检查项目设备",
                                  })
                                : deleteBlockedByDevices
                                  ? t("admin:project.deleteModal.blockedTitle", {
                                        name: deleteModal.project?.name,
                                        defaultValue: deleteModal.project?.name
                                            ? `项目“${deleteModal.project.name}”还有设备，暂不能删除`
                                            : "项目还有设备，暂不能删除",
                                    })
                                  : t("admin:project.deleteModal.title", {
                                        name: deleteModal.project?.name,
                                        defaultValue: deleteModal.project?.name
                                            ? `确认删除项目“${deleteModal.project.name}”？`
                                            : "确认删除项目？",
                                    })}
                        </div>
                        {deleteScopeChecking ? (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    marginBottom: 24,
                                }}
                            >
                                <Spin size="small" />
                                <Typography.Text
                                    style={{
                                        fontSize: 16,
                                        color: "#595959",
                                        lineHeight: 1.75,
                                    }}
                                >
                                    {t(
                                        "admin:project.deleteModal.checkingDescription",
                                        {
                                            defaultValue:
                                                "正在确认本项目和下级项目是否还有设备，请稍等。",
                                        },
                                    )}
                                </Typography.Text>
                            </div>
                        ) : deleteBlockedByDevices ? (
                            <Space
                                direction="vertical"
                                size={12}
                                style={{ width: "100%", marginBottom: 24 }}
                            >
                                {!canDeleteDevices ? (
                                    <Alert
                                        type="info"
                                        showIcon
                                        message={t(
                                            isDeletingRootProject
                                                ? "admin:project.deleteModal.mainDeleteDevicePermissionRequired"
                                                : "admin:project.deleteModal.deleteDevicePermissionRequired",
                                            {
                                                defaultValue:
                                                    isDeletingRootProject
                                                        ? "当前账号没有设备删除权限，请联系有权限的人员先处理设备。"
                                                        : "当前账号没有设备删除权限，只能调整设备归属或联系有权限的人员处理。",
                                            },
                                        )}
                                    />
                                ) : null}
                                <div
                                    style={{
                                        border: "1px solid #d9e8ff",
                                        borderRadius: 8,
                                        padding: 12,
                                        background: "#fbfdff",
                                    }}
                                >
                                    <Space
                                        wrap
                                        size={12}
                                        style={{
                                            width: "100%",
                                            marginBottom: 12,
                                        }}
                                    >
                                        <Input
                                            allowClear
                                            style={{ width: 220 }}
                                            value={deleteDeviceKeyword}
                                            placeholder={t(
                                                "admin:project.deleteModal.deviceSearchPlaceholder",
                                                {
                                                    defaultValue:
                                                        "搜索设备名称",
                                                },
                                            )}
                                            onChange={(event) =>
                                                setDeleteDeviceKeyword(
                                                    event.target.value,
                                                )
                                            }
                                            onPressEnter={
                                                applyDeleteDeviceFilters
                                            }
                                        />
                                        <Select
                                            style={{ width: 220 }}
                                            value={deleteDeviceProjectFilter}
                                            options={deleteDeviceProjectOptions}
                                            onChange={(value) =>
                                                setDeleteDeviceProjectFilter(
                                                    value,
                                                )
                                            }
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={applyDeleteDeviceFilters}
                                        >
                                            {t("common:buttons.search", {
                                                defaultValue: "查询",
                                            })}
                                        </Button>
                                        <Button
                                            onClick={resetDeleteDeviceFilters}
                                        >
                                            {t("common:buttons.reset", {
                                                defaultValue: "重置",
                                            })}
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                void selectAllDeleteDevices()
                                            }
                                            loading={selectingAllDeleteDevices}
                                            disabled={
                                                deleteDeviceLoading ||
                                                deleteDevicesSubmitting ||
                                                !canDeleteDevices
                                            }
                                        >
                                            {t(
                                                "admin:project.deleteModal.selectAllDevices",
                                                {
                                                    defaultValue:
                                                        "选择全部设备",
                                                },
                                            )}
                                        </Button>
                                    </Space>
                                    <Table<ProjectDeleteDeviceItem>
                                        rowKey="id"
                                        size="small"
                                        loading={deleteDeviceLoading}
                                        dataSource={deleteDeviceList}
                                        scroll={deleteDeviceTableScroll}
                                        rowSelection={{
                                            preserveSelectedRowKeys: true,
                                            selectedRowKeys:
                                                selectedDeleteDeviceIds,
                                            onChange:
                                                setSelectedDeleteDeviceIds,
                                            getCheckboxProps: () => ({
                                                disabled:
                                                    !canDeleteDevices ||
                                                    deleteDevicesSubmitting ||
                                                    selectingAllDeleteDevices,
                                            }),
                                        }}
                                        pagination={{
                                            current: deleteDeviceCurrentPage,
                                            pageSize: deleteDevicePageSize,
                                            total: deleteDeviceTotal,
                                            showSizeChanger: true,
                                            pageSizeOptions: PAGE_SIZE_OPTIONS,
                                            showTotal: (total) =>
                                                t(
                                                    "admin:project.deleteModal.devicePaginationTotal",
                                                    {
                                                        total,
                                                        defaultValue:
                                                            "共 {{total}} 台设备",
                                                    },
                                                ),
                                            onChange: (page, nextPageSize) => {
                                                if (!deleteModal.project) {
                                                    return;
                                                }
                                                const resolvedPageSize =
                                                    nextPageSize ??
                                                    deleteDevicePageSize;
                                                void loadDeleteDevices(
                                                    deleteModal.project,
                                                    {
                                                        page,
                                                        pageSize:
                                                            resolvedPageSize,
                                                        keyword:
                                                            appliedDeleteDeviceKeyword,
                                                        projectFilter:
                                                            appliedDeleteDeviceProjectFilter,
                                                    },
                                                );
                                            },
                                        }}
                                        columns={[
                                            {
                                                title: t(
                                                    "admin:project.deleteModal.deviceName",
                                                    {
                                                        defaultValue:
                                                            "设备名称",
                                                    },
                                                ),
                                                dataIndex: "name",
                                                ellipsis: true,
                                                render: (
                                                    value:
                                                        | string
                                                        | null
                                                        | undefined,
                                                ) =>
                                                    value?.trim() ||
                                                    t(
                                                        "admin:project.deleteModal.unnamedDevice",
                                                        {
                                                            defaultValue:
                                                                "未命名设备",
                                                        },
                                                    ),
                                            },
                                            {
                                                title: t(
                                                    "admin:project.deleteModal.projectName",
                                                    {
                                                        defaultValue:
                                                            "项目名称",
                                                    },
                                                ),
                                                dataIndex: "project_name",
                                                width: 220,
                                                ellipsis: true,
                                                render: (
                                                    _value:
                                                        | string
                                                        | null
                                                        | undefined,
                                                    record:
                                                        ProjectDeleteDeviceItem,
                                                ) =>
                                                    renderDeleteDeviceProjectName(
                                                        record,
                                                    ),
                                            },
                                        ]}
                                    />
                                </div>
                            </Space>
                        ) : null}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 12,
                            }}
                        >
                            {deleteScopeChecking ? (
                                <Button onClick={closeDeleteModal}>
                                    {t("common:buttons.cancel", {
                                        defaultValue: "取消",
                                    })}
                                </Button>
                            ) : deleteBlockedByDevices ? (
                                <>
                                    <Button onClick={closeDeleteModal}>
                                        {t("common:buttons.cancel", {
                                            defaultValue: "取消",
                                        })}
                                    </Button>
                                    <Button
                                        danger
                                        onClick={confirmDeleteSelectedDevices}
                                        loading={deleteDevicesSubmitting}
                                        disabled={
                                            !canDeleteDevices ||
                                            deleteDevicesSubmitting ||
                                            selectingAllDeleteDevices
                                        }
                                    >
                                        {t(
                                            "admin:project.deleteModal.deleteSelectedDevices",
                                            {
                                                defaultValue: "删除选中设备",
                                            },
                                        )}
                                    </Button>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Modal>

        </>
    );

    if (!canAccessProjectManagement) {
        return null;
    }

    return embedded ? (
        content
    ) : (
        <PageLayout
            title={pageTitle}
            breadcrumb={[
                { title: t("navigation:breadcrumb.projectManagement") },
                { title: t("navigation:breadcrumb.projectManagement") },
            ]}
        >
            {content}
        </PageLayout>
    );
};

export default ProjectManagementV1;

